import { useEffect, useRef, useState } from 'react'
import { Renderer, type FrameInput } from './Renderer'
import { useGraphFrame } from './useGraphFrame'
import { emitAfterDraw, requestRepaint } from './frameBus'
import { runCanvasLoop } from './cadence'
import { tapReach } from './tapReach'
import { Pointers } from './gesture'
import {
  captionDoubleTap,
  fromCaption,
  noteCaptionTap,
  setTouchTarget,
  takeLabelTap,
} from './labelTouch'
import { useStore } from '../core/store'
// `stretchKin` is the local variable holding this drag's set, so the import is
// renamed rather than the variable: the variable is what the rest of the
// gesture reads, and it is named for what it is.
import { clampStretch, layout, stretchKin as kinOf, stretchRange } from '../core/layout'
import { motion } from '../core/motion'

/**
 * Canvas host and pointer surface.
 *
 * The render loop reads store state imperatively rather than through React.
 * Subscribing a 60 fps loop to a React store would re-render the tree on every
 * pan; this way a drag touches exactly one `style` write and one canvas frame.
 */
export function NeuralCanvas() {
  const hostRef = useRef<HTMLDivElement | null>(null)
  const canvasRef = useRef<HTMLCanvasElement | null>(null)
  const rendererRef = useRef<Renderer | null>(null)
  const frameRef = useRef<FrameInput | null>(null)
  const [dragging, setDragging] = useState(false)
  // The same fact as `dragging`, kept where the gesture can read it. The pointer
  // effect below holds all of its state in a closure it builds once; reading the
  // React value there would make the effect depend on it, and the effect would
  // then tear down and rebuild mid-gesture — the moment the drag declares itself
  // — taking `down` and the rest of the gesture with it. The state is still what
  // renders; this is what the handlers decide on.
  const draggingRef = useRef(false)
  const [overNode, setOverNode] = useState(false)

  const {
    session,
    rev,
    childrenOf,
    visible,
    folded,
    focus,
    guideKeep,
    guideTargetId,
    palette,
    filters,
  } = useGraphFrame()

  // Keep the latest derived frame available to the imperative loop.
  useEffect(() => {
    const s = useStore.getState()
    frameRef.current = {
      session,
      childrenOf,
      visible,
      folded,
      focus,
      guideKeep,
      guideTargetId,
      selectedId: s.selectedId,
      hoverId: s.hoverId,
      activeTargets: s.activeTargets,
      filters,
      view: s.view,
      palette,
      busy: s.busy,
      rev,
      tempo: s.tempo,
      playhead: s.playhead,
      playheadAt: s.playheadAt,
      rings: s.showRings,
    }
  }, [session, childrenOf, visible, folded, focus, guideKeep, guideTargetId, palette, filters, rev])

  // A new palette is a new picture, and it is owed even under the sheet it was
  // chosen in: Settings holds the loop, and its glass shows the field through it.
  // See `cadence.ts`.
  useEffect(() => requestRepaint(), [palette])

  useEffect(() => {
    const canvas = canvasRef.current
    const host = hostRef.current
    if (!canvas || !host) return

    const renderer = new Renderer(canvas)
    rendererRef.current = renderer

    const ro = new ResizeObserver(() => {
      const r = host.getBoundingClientRect()
      if (r.width > 0 && r.height > 0) renderer.resize(r.width, r.height)
      // A resize invalidates the framing, but only while the camera is still
      // following; a user who panned somewhere deliberately keeps their view.
      followedRev = -1
      // And a resized canvas is a cleared one: it has to be drawn again even on a
      // loop that is holding.
      requestRepaint()
    })
    ro.observe(host)

    // Camera follow lives here rather than in an effect because this is the
    // only place with a size we can trust. A backgrounded tab reports 0×0 to
    // ResizeObserver and to getBoundingClientRect, and an effect that fires
    // then would silently give up on framing until the next structural change.
    let followedRev = -1
    // The insets the last follow framed against. The bottom band changes without
    // a `rev` bump (see `setViewBottom`), so the loop watches the object itself:
    // a new one is a new field, and the graph should breathe into it.
    let followedInset: unknown = null

    let lastT = performance.now()
    // One frame. Answers whether anything it drew is still live, growing or
    // moving — which, with the store's own live flags, is what keeps the loop on
    // its full rate. See `cadence.ts` for when the next frame comes.
    const frame = (t: number): boolean => {
      // Clamped: a long gap between frames should not advance the easing by a
      // full second in one step, but the clamp has to be loose enough that a
      // 30 fps machine still converges at roughly the intended rate.
      const dt = Math.min(120, Math.max(1, t - lastT))
      lastT = t

      const f = frameRef.current
      if (!f) {
        emitAfterDraw()
        return true
      }
      const s = useStore.getState()

      const { w, h } = renderer.size
      if (
        w > 0 &&
        h > 0 &&
        (followedRev !== f.rev || followedInset !== s.viewInset || s.frameOpening)
      ) {
        followedRev = f.rev
        followedInset = s.viewInset
        // Every revision is a candidate landing, and `follow` itself refuses on
        // a pinned view — the guardrail lives in the store, not in this caller.
        // The opening's end bumps no revision, so its request is asked here too,
        // and `follow` lowers it whichever way it answers.
        s.follow(w, h)
      }
      // A node somebody picked from a list, answered once, AFTER the follow: it
      // is judged against the frame the camera is heading for, so a pick that
      // the follow already brings into view moves nothing extra.
      if (s.viewAsk && w > 0 && h > 0) s.settleLook(w, h)

      // Ease toward the framing target. Exponential smoothing, so the camera
      // is always moving but never lurches — a graph that snaps to a new frame
      // on every arriving node is unreadable no matter how good the framing is.
      //
      // Except when the gap is large: the first frame of a session, or a tab
      // coming back after being suspended, would otherwise spend seconds
      // crawling across the canvas. Easing is for continuity, and there is no
      // continuity to preserve when you are nowhere near the target.
      //
      // Read fresh, not off `s`: the follow and the look above may have just
      // written a new target, and easing toward the one `s` held from before
      // them — then clearing it as arrived — threw the new one away. A phone
      // sheet rising the frame after a far jump lost its framing exactly so.
      const tgt = useStore.getState().viewTarget
      if (tgt) {
        const far =
          Math.abs(Math.log(tgt.k / Math.max(1e-4, s.view.k))) > 0.85 ||
          Math.hypot(tgt.x - s.view.x, tgt.y - s.view.y) > Math.max(w, 200) * 1.1
        if (far) {
          s.applyView(tgt, true)
        } else {
          const a = 1 - Math.exp(-dt / 420)
          const nk = s.view.k + (tgt.k - s.view.k) * a
          const nx = s.view.x + (tgt.x - s.view.x) * a
          const ny = s.view.y + (tgt.y - s.view.y) * a
          const arrived =
            Math.abs(tgt.k - nk) < tgt.k * 0.001 && Math.hypot(tgt.x - nx, tgt.y - ny) < 0.35
          s.applyView(arrived ? tgt : { k: nk, x: nx, y: ny }, arrived)
        }
      }
      // Live fields are pulled fresh; the memoised ones come from React.
      f.selectedId = s.selectedId
      f.hoverId = s.hoverId
      f.activeTargets = s.activeTargets
      f.view = s.view
      f.busy = s.busy
      f.tempo = s.tempo
      f.playhead = s.playhead
      f.playheadAt = s.playheadAt
      // Pulled per frame like the rest: the switch is a store write, which
      // wakes the loop, so the very next frame draws without the rings.
      f.rings = s.showRings
      // Wall-clock, not the rAF timestamp: node `birth` and `lastPulse` are
      // Date.now() values, and mixing the two clocks silently freezes every
      // growth and pulse animation.
      renderer.draw(f, Date.now(), dt)
      // The text overlay places itself from here, so that a caption and the
      // strand it names are always drawn from the same camera and the same
      // eased node positions. See frameBus.ts.
      emitAfterDraw()
      // Read after the draw, which may have just landed the camera: an ease that
      // arrived this frame is not live on the next.
      const after = useStore.getState()
      return (
        after.busy ||
        after.playing ||
        after.run?.phase === 'moving' ||
        after.viewTarget !== null ||
        after.viewAsk !== null ||
        motion.moving ||
        renderer.settling
      )
    }
    const stop = runCanvasLoop(host, frame)

    return () => {
      stop()
      ro.disconnect()
      rendererRef.current = null
    }
  }, [])

  /* ── pointer ───────────────────────────────────────────────────────── */

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return

    // Every variable below is the gesture in progress, and it lives in this
    // closure — so this effect must not re-subscribe while a gesture is
    // running. It used to depend on `dragging`, which is set by the gesture
    // itself the moment it passes the slop threshold: React re-rendered, the
    // effect tore itself down and rebuilt with `down` back to false, and every
    // event after the first was read as a hover. A pan moved 4px and stopped, a
    // pointerup was swallowed by `if (!down) return`, and the canvas was left
    // wearing the grabbing cursor with nothing being grabbed. The flag is a ref
    // now, because it is state the *loop* owns and only the cursor reads.
    //
    // Each pointer is kept by its own id (`Pointers`, D340): a second finger is a
    // pinch, not a new owner of the first one's gesture. `down` is "any pointer
    // is down", and `moved` is the lead's travel, which is what a tap is judged on.
    const pointers = new Pointers()
    let down = false
    let moved = 0

    /* ── stretch ──────────────────────────────────────────────────────
       A press that lands ON a node pulls that node along its own spoke;
       anywhere else, drag pans as it always has. Nothing else on this canvas
       moves a node, so the gesture is unambiguous once it has started — and
       the same 4px of slop that separates a pan from a click separates a
       stretch from one too, so tapping a node still just selects it. */

    /** Non-null while a node is being pulled. */
    let stretchId: string | null = null
    /** Where this drag has got to, in world units off the layout's own radius:
     *  the raw running total, bounded but never snapped. Snapping the
     *  accumulator is what made a slow drag move nothing — see `stretchRange`. */
    let stretchRaw = 0
    /** What that total means once the snap is applied: the value the layout is
     *  drawn at, and the value the release commits. */
    let stretchAt = 0
    /** The spoke, in world space. Fixed at press: the angle does not change. */
    let spokeX = 1
    let spokeY = 0
    /** The node and everything under it — what has to keep up with the pointer. */
    let stretchKin: string[] = []

    const local = (e: PointerEvent | WheelEvent) => {
      const r = canvas.getBoundingClientRect()
      return { x: e.clientX - r.left, y: e.clientY - r.top }
    }

    /** Puts back a stretch the gesture armed and will not commit. The drag so far
     *  laid the subtree out at a stretch nobody pressed for; laid out again from
     *  the stored values, it is where the store has it. */
    const dropStretch = () => {
      const f = frameRef.current
      if (stretchId && f) {
        layout(f.session.nodes, f.session.order)
        motion.snap(f.session.nodes, stretchKin)
      }
      stretchId = null
      stretchKin = []
    }

    const declareDrag = () => {
      if (draggingRef.current) return
      draggingRef.current = true
      setDragging(true)
    }

    const onDown = (e: PointerEvent) => {
      if (e.button !== 0) return
      // A pointer still held here whose capture is gone lost its up on the way
      // (D340): a live one keeps the capture its press set. Left in, it would
      // make this press a pinch, or an extra that drags what it armed.
      for (const id of pointers.ids())
        if (!canvas.hasPointerCapture(id)) {
          pointers.up(id)
          takeLabelTap(id)
        }
      // A press the browser itself delivered here never began on a caption — a
      // caption's is replayed, untrusted — so any entry under its id is stale.
      if (e.isTrusted) takeLabelTap(e.pointerId)
      const landed = pointers.down(e.pointerId, e.clientX, e.clientY)
      canvas.setPointerCapture(e.pointerId)
      down = true
      if (landed === 'pinch') {
        // Two fingers are a pinch, never a stretch: whatever the first one
        // armed is put back, and from here the gesture moves only the view.
        dropStretch()
        declareDrag()
        return
      }
      if (landed !== 'lead') return
      moved = 0
      // A new press forgets the last caption tap; its own end notes it again if
      // it is one, so only a real second tap makes a caption's double-tap.
      noteCaptionTap(null)
      // A lead lands on a clean slate, even after a press whose up never came:
      // its stretch is put back rather than carried into this one, and its
      // grabbing cursor goes.
      dropStretch()
      if (draggingRef.current) {
        draggingRef.current = false
        setDragging(false)
      }
      const f = frameRef.current
      const r = rendererRef.current
      // ⌥ is the fold gesture, and it is resolved on release. Arming a stretch
      // under it would move the branch the user is about to put away. And a
      // finger that landed on a caption aimed at the words, not at whatever dot
      // lies under them: from there a drag pans (`labelTouch.ts`, D340).
      if (!f || !r || e.altKey || fromCaption(e.pointerId)) return
      const p = local(e)
      // `hitTest` only, deliberately not `hitStrand`: a strand is a long,
      // generous target, and claiming it would take the pan away from most of
      // the canvas — including every gap between two branches, which is where a
      // hand naturally reaches to move the view.
      const hit = r.hitTest(f, p.x, p.y)
      const n = hit ? f.session.nodes[hit] : null
      // The seed is the origin: no spoke, and every other position is measured
      // from it.
      if (!n || n.parentId === null) return

      stretchId = n.id
      stretchRaw = n.stretch ?? 0
      stretchAt = stretchRaw
      spokeX = Math.cos(n.angle)
      spokeY = Math.sin(n.angle)
      // Everything the layout will move with it — the subtree, and any bridge
      // seated off this node. Not `childrenOf`, which knows only `parentId`:
      // see `stretchKin`. Collected once here rather than per move; a drag
      // cannot change it.
      stretchKin = kinOf(f.session.nodes, f.session.order, n.id)
    }

    /** One pointer move, resolved onto the spoke and laid out. */
    const pull = (dx: number, dy: number) => {
      const f = frameRef.current
      if (!f || !stretchId) return
      const { k } = useStore.getState().view
      // The screen delta projected onto the spoke, then out of screen units and
      // into world ones. The flat camera is a scale and a translate with no
      // rotation, so the world axes and the screen axes are the same axes.
      // The running total absorbs the delta unsnapped; the snap is applied to
      // what is read off it, never to the total itself. Snapping here zeroed
      // every increment under the threshold, so a slow drag never accumulated.
      stretchRaw = stretchRange(stretchRaw + (dx * spokeX + dy * spokeY) / k)
      stretchAt = clampStretch(stretchRaw)
      // Laid out straight onto the node objects instead of through the store. A
      // commit per move would re-render React and re-derive the whole visibility
      // pass sixty times a second — and because the camera refits on every `rev`,
      // the graph would sail away from the node being held. The render loop reads
      // these coordinates and nothing else, so this is all it takes to see it move.
      layout(f.session.nodes, f.session.order, { id: stretchId, stretch: stretchAt })
      motion.snap(f.session.nodes, stretchKin)
    }

    const onMove = (e: PointerEvent) => {
      const f = frameRef.current
      const r = rendererRef.current
      if (!f || !r) return

      if (down) {
        const step = pointers.move(e.pointerId, e.clientX, e.clientY)
        if (!step) return
        const st = useStore.getState()
        if (step.kind === 'pinch') {
          // The reader's own zoom and pan, through the same two writes the wheel
          // and a drag make — so a pinch pins the view as they do (invariant 11).
          const r0 = canvas.getBoundingClientRect()
          if (step.factor !== 1) st.zoomBy(step.factor, step.cx - r0.left, step.cy - r0.top)
          if (step.dx !== 0 || step.dy !== 0) st.nudgeView(step.dx, step.dy)
          return
        }
        const { dx, dy } = step
        // The finger a pinch left behind drags the view from where it is, with
        // no slop to cross: the gesture declared itself when the second landed.
        if (pointers.pinched) {
          st.nudgeView(dx, dy)
          return
        }
        moved += Math.abs(dx) + Math.abs(dy)
        if (moved > 4) {
          declareDrag()
          if (stretchId) pull(dx, dy)
          else st.nudgeView(dx, dy)
        }
        return
      }

      const p = local(e)
      const hit = r.hitTest(f, p.x, p.y) ?? r.hitStrand(f, p.x, p.y)
      useStore.getState().hover(hit)
      setOverNode(!!hit)
    }

    const onUp = (e: PointerEvent) => {
      if (!pointers.has(e.pointerId)) return
      // Taken on every end, so no caption press outlives its own.
      const caption = takeLabelTap(e.pointerId)
      if (canvas.hasPointerCapture(e.pointerId)) canvas.releasePointerCapture(e.pointerId)
      // One finger of several lifting ends nothing: the rest carry the gesture.
      if (!pointers.up(e.pointerId)) return
      down = false
      draggingRef.current = false
      setDragging(false)
      // A pinch ends in no tap and no stretch, however little its last finger
      // travelled — its stretch was put back the moment the second finger
      // landed. Neither does a press the system took away (pointercancel, an
      // edge swipe, or a capture lost with no up): it was never a tap.
      if (pointers.pinched || e.type !== 'pointerup') {
        dropStretch()
        return
      }
      const held = stretchId
      stretchId = null
      stretchKin = []

      // A drag is not a click. 4px of slop keeps trackpads honest.
      if (moved > 4) {
        // The one commit a stretch makes. The graph has been at these
        // coordinates for the whole drag; this is what makes them survive the
        // next arrival, the next fold, and a reload.
        if (held) useStore.getState().stretchNode(held, stretchAt)
        return
      }
      // A tap that began on a caption is the caption's tap: its node, whatever
      // lies under the fingertip (D340).
      // Remembered either way, so the browser's double-click that may follow
      // knows whose it is (`captionDoubleTap`).
      noteCaptionTap(caption ?? null)
      if (caption) {
        const st = useStore.getState()
        // ⌥ folds, as it does on the caption itself — except the seed, which
        // `toggleCollapse` refuses, so ⌥ there falls through to a select.
        if (e.altKey && st.session.nodes[caption]?.parentId) {
          st.toggleCollapse(caption)
          return
        }
        st.select(caption)
        st.setRail(true)
        return
      }
      const f = frameRef.current
      const r = rendererRef.current
      if (!f || !r) return
      const p = local(e)
      // A finger's tap reaches further than a cursor's click (`tapReach`, D335).
      // The press above that armed a stretch did not: that reach decides what a
      // drag moves, and it stays narrow so the gaps keep panning.
      const hit = r.hitTest(f, p.x, p.y, tapReach(e)) ?? r.hitStrand(f, p.x, p.y)
      // hitStrand returns the child a strand terminates at, so ⌥-clicking the
      // strand *into* a branch folds that branch — a second, generous target
      // for the same gesture.
      if (e.altKey && hit) {
        useStore.getState().toggleCollapse(hit)
        return
      }
      useStore.getState().select(hit)
      if (hit) useStore.getState().setRail(true)
    }

    const onDouble = (e: PointerEvent) => {
      // A double-tap on a caption is the caption's: it focuses that branch, as
      // a double-click on the words always has (D340).
      const caption = captionDoubleTap()
      if (caption) {
        const st = useStore.getState()
        st.setFocus(st.focusId === caption ? null : caption)
        return
      }
      const f = frameRef.current
      const r = rendererRef.current
      if (!f || !r) return
      const p = local(e)
      const hit = r.hitTest(f, p.x, p.y, tapReach(e))
      const st = useStore.getState()
      if (hit) {
        // Double-click toggles focus: isolate this branch, or release it.
        st.setFocus(st.focusId === hit ? null : hit)
        st.select(hit)
      } else {
        st.setFocus(null)
        const rect = canvas.getBoundingClientRect()
        st.frame(rect.width, rect.height)
      }
    }

    const onWheel = (e: WheelEvent) => {
      e.preventDefault()
      const p = local(e)
      const st = useStore.getState()
      // Trackpad pinch arrives as ctrlKey+wheel; plain wheel pans, which is
      // what a map-like surface should do on a laptop.
      if (e.ctrlKey || e.metaKey) {
        st.zoomBy(Math.exp(-e.deltaY * 0.01), p.x, p.y)
      } else if (e.shiftKey) {
        st.nudgeView(-e.deltaY, 0)
      } else if (Math.abs(e.deltaX) > Math.abs(e.deltaY) * 0.6) {
        st.nudgeView(-e.deltaX, -e.deltaY)
      } else {
        st.zoomBy(Math.exp(-e.deltaY * 0.0022), p.x, p.y)
      }
    }

    const onLeave = () => {
      useStore.getState().hover(null)
      setOverNode(false)
    }

    // A finger that lands on a caption is handed here (`labelTouch.ts`, D340).
    const releaseTouch = setTouchTarget(canvas)
    canvas.addEventListener('pointerdown', onDown)
    canvas.addEventListener('pointermove', onMove)
    canvas.addEventListener('pointerup', onUp)
    canvas.addEventListener('pointercancel', onUp)
    // A capture lost without an up is the press ending all the same. After a
    // real up it finds nothing held, so it does nothing.
    canvas.addEventListener('lostpointercapture', onUp)
    canvas.addEventListener('pointerleave', onLeave)
    canvas.addEventListener('dblclick', onDouble as unknown as EventListener)
    canvas.addEventListener('wheel', onWheel, { passive: false })

    return () => {
      releaseTouch()
      canvas.removeEventListener('pointerdown', onDown)
      canvas.removeEventListener('pointermove', onMove)
      canvas.removeEventListener('pointerup', onUp)
      canvas.removeEventListener('pointercancel', onUp)
      canvas.removeEventListener('lostpointercapture', onUp)
      canvas.removeEventListener('pointerleave', onLeave)
      canvas.removeEventListener('dblclick', onDouble as unknown as EventListener)
      canvas.removeEventListener('wheel', onWheel)
    }
  }, [])

  return (
    <div ref={hostRef} style={{ position: 'absolute', inset: 0 }}>
      <canvas
        ref={canvasRef}
        data-drag={dragging || undefined}
        data-hot={overNode && !dragging ? true : undefined}
        aria-label="Reasoning graph"
        role="img"
      />
    </div>
  )
}

/** Shared by the overlay furniture that needs the stage's pixel size. */
export function useStageSize(ref: React.RefObject<HTMLElement | null>) {
  const [size, setSize] = useState({ w: 0, h: 0 })
  useEffect(() => {
    const el = ref.current
    if (!el) return
    const measure = () => {
      const r = el.getBoundingClientRect()
      // A backgrounded tab reports 0×0; keeping the last real size stops the
      // overlay furniture from collapsing when the user switches away.
      if (r.width > 0 && r.height > 0) setSize({ w: r.width, h: r.height })
    }
    const ro = new ResizeObserver(measure)
    ro.observe(el)
    // The 0×0 guard has a corner: a resize that lands while the document is
    // hidden fires the observer against a zero rect, the update is skipped,
    // and — because the observer only fires on further size CHANGES — the
    // stale size then survives the return to visibility until the next real
    // resize. Everything derived from it (the minimap gate, the transport
    // clamp, zoom centres) reads a window that no longer exists. Re-measuring
    // on visibilitychange closes the loop: the guard keeps its job while
    // hidden, and the first visible frame heals the size.
    document.addEventListener('visibilitychange', measure)
    return () => {
      ro.disconnect()
      document.removeEventListener('visibilitychange', measure)
    }
  }, [ref])
  return size
}
